// Noyau — forme normale légère, filtrage AC, réécriture, gestes « builtin » génériques.
//
// Rien de métier ici : les types de nœuds sont lus dans la table (props assoc / comm, unit,
// relation, flip) et leur RÔLE arithmétique est retrouvé par leur `eval` (primitive du noyau :
// le type dont eval = 'add' joue l'addition, 'mul' la multiplication, 'neg' l'opposé, 'div',
// 'pow'). Seules les formes Num / Sym / Var et le nombre −1 sont connus du noyau (CONTRACT §1, §6).

import { num, Q, isLeaf, symbols, gcd } from './ast.js';
import { exactValue, vectorPoints, evaluate, constValue } from './equiv.js';
import {
  varOf,
  limitSym,
  extendedAst,
  growth,
  rolesOf,
  indeterminateOf,
  pointOf,
  signedZero,
  sideZeroAst,
} from './calculus.js';
const toNum = (x) => (typeof x === 'number' ? x : Number(x.p) / Number(x.q));

// ── Rôles arithmétiques lus dans la table ─────────────────────────────────
const ROLES = new WeakMap();
export function roles(table) {
  let r = ROLES.get(table);
  if (r) return r;
  r = {};
  for (const [t, spec] of Object.entries(table.nodes))
    if (spec.eval && !r[spec.eval]) r[spec.eval] = t;
  ROLES.set(table, r);
  return r;
}
const propsOf = (table, t) => table.nodes[t]?.props || [];
const isAC = (table, t) => {
  const p = propsOf(table, t);
  return p.includes('assoc') && p.includes('comm');
};
function unitOf(table, t) {
  const u = table.nodes[t]?.unit;
  return typeof u === 'number' && Number.isInteger(u) ? num(u) : null;
}
const mk = (t, k, name) => (name !== undefined ? { t, k, name } : { t, k });

// Parcours pré-ordre sans allocation : f(nœud, chemin) — le chemin est une pile partagée, à ne pas garder.
export function visit(n, f, path = []) {
  f(n, path);
  if (!isLeaf(n))
    for (let i = 0; i < n.k.length; i++) {
      path.push(i);
      visit(n.k[i], f, path);
      path.pop();
    }
}
function setAt(n, path, v, d = 0) {
  if (d === path.length) return v;
  const k = n.k.slice();
  k[path[d]] = setAt(n.k[path[d]], path, v, d + 1);
  return mk(n.t, k, n.name);
}

// ── Clé canonique (mémoïsée, hash-consée ; ignore l'écriture décimale) ──────
// La clé d'un nœud composé s'écrit avec les NUMÉROS internés des clés de ses enfants : elle reste courte
// (O(nombre d'enfants)) au lieu de recopier tout le sous-arbre. Deux arbres de même structure ont la même
// clé dans un même processus ; l'ordre des enfants comm (trié par clé) est donc canonique aussi.
const KEYS = new WeakMap();
const INTERN = new Map();
const intern = (k) => {
  let i = INTERN.get(k);
  if (i === undefined) {
    i = INTERN.size;
    INTERN.set(k, i);
  }
  return i;
};
export function keyOf(n) {
  switch (n.t) {
    case 'Num':
      return n.q === 1 ? `${n.p === 0 ? 0 : n.p}` : `${n.p}/${n.q}`;
    case 'Sym':
      return n.name;
    case 'Var':
      return `?${n.name}`;
  }
  let k = KEYS.get(n);
  if (k === undefined) {
    let s = `${n.t}${n.name !== undefined ? ':' + n.name : ''}(`;
    for (let i = 0; i < n.k.length; i++) s += (i ? ',' : '') + intern(keyOf(n.k[i]));
    k = s + ')';
    KEYS.set(n, k);
  }
  return k;
}
const cmpKey = (a, b) => {
  const x = keyOf(a),
    y = keyOf(b);
  return x < y ? -1 : x > y ? 1 : 0;
};

// ── Forme normale légère ──────────────────────────────────────────────────
// • aplatissement des nœuds `assoc` ; tri des enfants des nœuds `comm` (clé canonique) ;
// • Neg(Num) → Num négatif ; Neg(Neg x) → x ;
// • signes d'un produit (type eval 'mul') sortis devant : 2 × (−3) et −2 × 3 → Neg(Mul[2, 3]),
//   un −1 facteur disparaît (−1 × x → −x) : la règle des signes n'est pas un geste ;
// • signes d'un quotient (type eval 'div') sortis devant : −(2 × 5)/(3 × 5), −((2 × 5) ÷ (3 × 5)) et
//   (−10)/15 → Neg(Div(…)). Une fraction RESTE une fraction (Div) : 2/3 n'est pas le nombre 2/3, leur
//   égalité de valeur est une question d'équivalence ou de geste (« Calculer »), pas de forme ;
// • nœud assoc à un seul enfant → l'enfant.
// Rien d'autre : ni calcul, ni regroupement, ni suppression de l'élément neutre (ce sont des gestes).
const LIGHT = new WeakSet();
export function light(n, table) {
  if (isLeaf(n) || LIGHT.has(n)) return n;
  const R = roles(table);
  let k = n.k.map((c) => light(c, table));
  let out;
  if (n.t === R.neg && k.length === 1) out = negL(k[0], R);
  else if (n.t === R.div && k.length === 2) {
    let sign = 1;
    k = k.map((c) => {
      if (c.t === R.neg) {
        sign = -sign;
        return c.k[0];
      }
      if (c.t === 'Num' && c.p < 0) {
        sign = -sign;
        return num(-c.p, c.q);
      }
      return c;
    });
    out = mk(n.t, k, n.name);
    LIGHT.add(out);
    if (sign < 0) out = negL(out, R);
  } else {
    const props = propsOf(table, n.t);
    if (props.includes('assoc'))
      k = k.flatMap((c) => (c.t === n.t && c.name === n.name ? c.k : [c]));
    let sign = 1;
    if (n.t === R.mul) {
      const k2 = [];
      for (const c of k) {
        if (c.t === R.neg) {
          sign = -sign;
          const inner = c.k[0];
          if (inner.t === n.t) k2.push(...inner.k);
          else k2.push(inner);
        } else if (c.t === 'Num' && c.p < 0) {
          sign = -sign;
          // « 3 × (−1) », « 1 × (−1) » restent des calculs à faire : on ne garde le −1 en signe seul que devant
          // une écriture (ou d'autres −1 : « (−1) × (−1) », règle des signes)
          if (!(c.p === -1 && c.q === 1) || k.some((o) => o !== c && o.t === 'Num' && o.p >= 0))
            k2.push(num(-c.p, c.q));
        } else k2.push(c);
      }
      k = k2;
    }
    if (props.includes('comm')) k = k.slice().sort(cmpKey);
    if (props.includes('assoc') && k.length === 1) out = k[0];
    else if (props.includes('assoc') && k.length === 0) out = unitOf(table, n.t) ?? num(1);
    else out = mk(n.t, k, n.name);
    if (sign < 0) out = negL(out, R);
  }
  if (!isLeaf(out)) LIGHT.add(out);
  return out;
}
const isInt = (x) => x.t === 'Num' && x.q === 1;
function negL(c, R) {
  if (c.t === 'Num') return num(-c.p, c.q);
  if (c.t === R.neg) return c.k[0];
  const o = { t: R.neg, k: [c] };
  LIGHT.add(o);
  return o;
}
export function lightEqual(a, b, table) {
  return keyOf(light(a, table)) === keyOf(light(b, table));
}

// Vue « produit » d'un opposé : Neg(Mul[a, b]) se filtre comme Mul[−1, a, b].
const M1 = num(-1);
function mulView(n, R) {
  const inner = n.k[0];
  return { t: R.mul, k: [M1, ...(inner.t === R.mul ? inner.k : [inner])] };
}
// Vues d'un opposé pour un motif de type pt : produit (ci-dessus), quotient Neg(Div(a, b)) → Div(−a, b).
function negView(n, pt, R) {
  if (n.t !== R.neg) return null;
  if (pt === R.mul) return mulView(n, R);
  if (pt === R.div && n.k[0].t === R.div) {
    const [a, b] = n.k[0].k;
    return { t: R.div, k: [negL(a, R), b] };
  }
  return null;
}

// ── Filtrage ──────────────────────────────────────────────────────────────
// match(pattern, node, table) → liste de liaisons { nomVar: AST } (motif et nœud en forme légère).
// Nœud assoc+comm : le motif P(p1..pm) filtre N(n1..nn), n ≥ m, en choisissant m enfants distincts dans
// n'importe quel ordre ; à la RACINE, les enfants restants sont rendus dans `$rest` (ré-attachés au
// résultat) ; à toute profondeur, une variable du motif peut aussi absorber le reste (≥ 2 enfants)
// comme un nœud du même type (?k filtre « −x » dans −x(x − 4)).
// Un motif Neg(p) filtre un Neg(n) ou un nombre négatif ; un motif produit filtre un Neg(produit)
// vu comme Mul[−1, …].
export function match(pattern, nd, table, b = {}, root = true) {
  return m(pattern, nd, b, root, table, roles(table));
}
function m(p, n, b, root, table, R) {
  switch (p.t) {
    case 'Var':
      if (Object.prototype.hasOwnProperty.call(b, p.name))
        return keyOf(b[p.name]) === keyOf(n) ? [b] : [];
      return [{ ...b, [p.name]: n }];
    case 'Num':
      return n.t === 'Num' && n.p === p.p && n.q === p.q ? [b] : [];
    case 'Sym':
      return n.t === 'Sym' && n.name === p.name ? [b] : [];
  }
  if (p.t === R.neg) {
    if (n.t === R.neg) return m(p.k[0], n.k[0], b, false, table, R);
    if (n.t === 'Num' && n.p < 0) return m(p.k[0], num(-n.p, n.q), b, false, table, R);
    return [];
  }
  let x = n;
  if (p.t !== n.t) {
    x = negView(n, p.t, R);
    if (!x) return [];
  }
  if (p.name !== x.name) return [];
  if (isAC(table, p.t)) return acMatch(p, x, b, root, table, R);
  if (p.k.length !== x.k.length) return [];
  let bs = [b];
  for (let i = 0; i < p.k.length && bs.length; i++)
    bs = bs.flatMap((bb) => m(p.k[i], x.k[i], bb, false, table, R));
  return bs;
}
function acMatch(p, x, b, root, table, R) {
  const pk = p.k,
    nk = x.k;
  if (nk.length < pk.length) return [];
  const order = [...pk.keys()].sort((i, j) => (pk[i].t === 'Var') - (pk[j].t === 'Var'));
  const used = new Array(nk.length).fill(false);
  const out = [];
  function rec(oi, bb, skip, finish) {
    if (oi === order.length) {
      finish(bb);
      return;
    }
    const pi = order[oi];
    if (pi === skip) {
      rec(oi + 1, bb, skip, finish);
      return;
    }
    for (let j = 0; j < nk.length; j++) {
      if (used[j]) continue;
      const r = m(pk[pi], nk[j], bb, false, table, R);
      if (!r.length) continue;
      used[j] = true;
      for (const b2 of r) rec(oi + 1, b2, skip, finish);
      used[j] = false;
    }
  }
  const rest = () => nk.filter((_, j) => !used[j]);
  rec(0, b, -1, (bb) => {
    const r = rest();
    if (!r.length) out.push(bb);
    else if (root) out.push({ ...bb, $rest: r });
  });
  if (nk.length > pk.length) {
    pk.forEach((pc, vi) => {
      if (pc.t !== 'Var') return;
      rec(0, b, vi, (bb) => {
        const r = rest();
        if (r.length < 2) return;
        out.push(...m(pc, light(mk(x.t, r, x.name), table), bb, false, table, R));
      });
    });
  }
  return out;
}

export function instantiate(rhs, b) {
  if (rhs.t === 'Var') {
    const v = b[rhs.name];
    if (!v) throw new Error(`variable ?${rhs.name} non liée`);
    return v;
  }
  if (isLeaf(rhs)) return rhs;
  return mk(
    rhs.t,
    rhs.k.map((c) => instantiate(c, b)),
    rhs.name,
  );
}

// ── Règles préparées (motifs en forme légère, pré-filtre vérifié) ─────────
const PREP = new WeakMap();
export function prepareRule(rule, table) {
  let P = PREP.get(rule);
  if (P) return P;
  const lhs = rule.lhs ? light(rule.lhs, table) : null;
  const rhs = rule.rhs ? light(rule.rhs, table) : null;
  // Le pré-filtre généré a été écrit pour la forme légère du compilateur ; on ne s'en sert que s'il
  // accepte le motif lui-même dans NOTRE forme légère (sinon il pourrait rejeter à tort).
  let pre = null;
  if (rule.pre && lhs) {
    try {
      if (rule.pre(lhs)) pre = rule.pre;
    } catch {
      pre = null;
    }
  }
  P = { lhs, rhs, pre, free: rule.free || [] };
  PREP.set(rule, P);
  return P;
}

function applyAt(n, path, ast, rule, P, table, R, K, out) {
  const p = P.lhs;
  let x = n;
  if (p.t !== 'Var' && p.t !== n.t) {
    const v = negView(n, p.t, R);
    if (v) x = v;
    else if (!(p.t === R.neg && n.t === 'Num' && n.p < 0)) return;
  }
  if (P.pre) {
    let ok = true;
    try {
      ok = P.pre(x);
    } catch {
      ok = true;
    }
    if (!ok) return;
  }
  // variables libres du rhs : valeurs candidates lues dans la ligne visée (K.free, voir freeCandidates)
  const frees = P.free.length ? K.free?.get(rule) || [] : [null];
  if (!frees.length) return;
  const seenB = new Set();
  for (const b0 of match(p, x, table)) {
    // liaisons identiques (fréquentes avec l'AC : ordres différents, mêmes valeurs) : un seul candidat
    let sig = '';
    for (const v in b0)
      sig +=
        v === '$rest' ? '|' + b0.$rest.map(keyOf).sort().join(',') : v + '=' + keyOf(b0[v]) + ';';
    if (seenB.has(sig)) continue;
    seenB.add(sig);
    for (const fb of frees) {
      const b = fb ? { ...b0, ...fb } : b0;
      if (rule.guard) {
        let ok = false;
        try {
          ok = rule.guard(b, K);
        } catch {
          ok = false;
        }
        if (!ok) continue;
      }
      let r = instantiate(P.rhs, b);
      // règle « avec le calcul » : les morceaux du résultat calculés (« (1 + 1) ÷ 3 → 2 ÷ 3 », « 10^(7 − 4) →
      // 10³ »), pas le résultat lui-même — une variante de plus
      const rc =
        rule.withCalc && !isLeaf(r)
          ? mk(
              r.t,
              r.k.map((c) => foldAll(light(c, table), table)),
              r.name,
            )
          : null;
      if (b.$rest) r = mk(x.t, [r, ...b.$rest], x.name);
      out.push(light(setAt(ast, path, r), table));
      if (rc)
        out.push(light(setAt(ast, path, b.$rest ? mk(x.t, [rc, ...b.$rest], x.name) : rc), table));
    }
  }
}

// Variables libres (`rule.free`, CONTRACT §8) : on filtre le rhs de chaque règle concernée sur tous les
// sous-arbres de la ligne VISÉE ; les valeurs prises par les variables libres sont les candidates.
// → Map(règle → [{ k: AST }, …]) à passer dans K.free.
export function freeCandidates(engine, targetL) {
  const table = engine.table,
    out = new Map();
  for (const rule of engine.rules) {
    if (!rule.free?.length || !rule.rhs) continue;
    const P = prepareRule(rule, table);
    const seen = new Set(),
      list = [];
    visit(targetL, (s) => {
      for (const b of match(P.rhs, s, table)) {
        if (!P.free.every((v) => b[v])) continue;
        const fb = Object.fromEntries(P.free.map((v) => [v, b[v]]));
        const k = P.free.map((v) => keyOf(fb[v])).join('|');
        if (!seen.has(k)) {
          seen.add(k);
          list.push(fb);
        }
      }
    });
    out.set(rule, list);
  }
  return out;
}

export function dedupe(list, keyFn = (x) => keyOf(x.ast ?? x)) {
  const seen = new Set(),
    out = [];
  for (const x of list) {
    const k = keyFn(x);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(x);
    }
  }
  return out;
}

// rewriteOnce(ast, rule, engine, K) → candidats (AST en forme légère), toutes positions.
// K = { pred, …données du modèle } (voir judge.js) ; `rule.pre` préfiltre, `rule.guard(b, K)` garde.
export function rewriteOnce(ast, rule, engine, K = {}) {
  const table = engine.table;
  const L = light(ast, table);
  if (rule.kind === 'builtin') {
    const s = STRATEGIES[rule.builtin];
    return s ? dedupe(s(L, table, K)) : [];
  }
  const P = prepareRule(rule, table),
    R = roles(table),
    out = [];
  if (!P.lhs || !P.rhs) return [];
  visit(L, (n, path) => applyAt(n, path, L, rule, P, table, R, K, out));
  return dedupe(out);
}

// Index par type de racine du motif EN FORME LÉGÈRE (les racines peuvent différer de engine.index).
const IDX = new WeakMap();
function ruleIndex(engine, kind) {
  let byKind = IDX.get(engine);
  if (!byKind) {
    byKind = {};
    IDX.set(engine, byKind);
  }
  if (byKind[kind]) return byKind[kind];
  const idx = new Map(),
    star = [];
  for (const rule of engine.rules) {
    if (rule.kind !== kind || !rule.lhs || !rule.rhs) continue;
    const P = prepareRule(rule, engine.table);
    if (P.lhs.t === 'Var') star.push(rule);
    else {
      if (!idx.has(P.lhs.t)) idx.set(P.lhs.t, []);
      idx.get(P.lhs.t).push(rule);
    }
  }
  return (byKind[kind] = { idx, star });
}

// Toutes les réécritures d'un AST léger par les règles d'un genre ('rewrite' | 'trap') : un seul parcours.
export function rewriteAll(L, engine, K, kind = 'rewrite', filter = null) {
  const table = engine.table,
    R = roles(table);
  const { idx, star } = ruleIndex(engine, kind);
  const out = [];
  visit(L, (n, path) => {
    if (n.t === 'Var') return;
    let rules = idx.get(n.t) || [];
    if (n.t === R.neg && idx.has(R.mul)) rules = rules.concat(idx.get(R.mul));
    if (n.t === R.neg && n.k[0].t === R.div && idx.has(R.div)) rules = rules.concat(idx.get(R.div));
    if (n.t === 'Num' && n.p < 0 && idx.has(R.neg)) rules = rules.concat(idx.get(R.neg));
    if (star.length) rules = rules.concat(star);
    for (const rule of rules) {
      if (filter && !filter(rule)) continue;
      const tmp = [];
      applyAt(n, path, L, rule, prepareRule(rule, table), table, R, K, tmp);
      for (const ast of tmp) out.push({ ast, rule });
    }
  });
  return out;
}

// ── Outils numériques sur l'arbre ─────────────────────────────────────────

// Un nombre rationnel exact → AST : entier, ou fraction Div(p, q) (signe devant).
// Un calcul sur des décimaux donne un décimal quand l'écriture est finie (0,3 × 0,05 → 0,015), comme l'élève l'écrit.
const hasDecLit = (n) => (n.t === 'Num' ? !!n.dec : (n.k || []).some(hasDecLit));
function decNum(v) {
  if (v.q === 1) return num(v.p);
  let q = v.q,
    k2 = 0,
    k5 = 0;
  while (q % 2 === 0) {
    q /= 2;
    k2++;
  }
  while (q % 5 === 0) {
    q /= 5;
    k5++;
  }
  if (q !== 1) return null;
  const k = Math.max(k2, k5);
  const digits = ((BigInt(Math.abs(v.p)) * 10n ** BigInt(k)) / BigInt(v.q))
    .toString()
    .padStart(k + 1, '0');
  const dec = `${v.p < 0 ? '−' : ''}${digits.slice(0, -k)},${digits.slice(-k)}`.replace(
    /,?0+$/,
    '',
  );
  return { ...num(v.p, v.q), dec };
}
function pairFolds(n, table, fractions = false) {
  if (isLeaf(n) || !isAC(table, n.t)) return [];
  // une somme / un produit d'au moins trois termes dont deux nombres : on calcule DEUX nombres, n'importe
  // lesquels (les termes sont rangés dans un ordre fixe : « voisins » à l'écrit ne l'est pas forcément ici)
  const R = roles(table);
  // mêmes littéraux que foldNode : dans une somme, des nombres seulement (une somme de fractions est le travail
  // des règles de fractions)
  const lit = (c) => (n.t === R.add && !fractions ? c.t === 'Num' : isLit(c, R));
  const idx = n.k.map((c, i) => (lit(c) ? i : -1)).filter((i) => i >= 0);
  if (idx.length < 2 || n.k.length < 3) return [];
  const out = [];
  for (let x = 0; x < idx.length; x++)
    for (let y = x + 1; y < idx.length; y++) {
      const a = n.k[idx[x]],
        b = n.k[idx[y]];
      const v = exactValue(mk(n.t, [a, b], n.name), table);
      if (!v) continue;
      const r = a.dec || b.dec ? (decNum(v) ?? ratAst(v, table)) : ratAst(v, table);
      out.push(mk(n.t, [...n.k.filter((_, i) => i !== idx[x] && i !== idx[y]), r], n.name));
    }
  return out;
}

// Une passe de calculs : chaque nœud qui se calcule en une opération est remplacé par son résultat (en décimal
// court si `dec` et si c'est possible) ; sinon on descend dans ses enfants. `count` = nombre d'endroits calculés.
function foldLayer(n, table, fractions, dec) {
  if (isLeaf(n)) return { ast: n, count: 0 };
  const r = foldNode(n, table, fractions);
  if (r) return { ast: (dec && decimalOf(n, r, table)) || r, count: 1 };
  let count = 0;
  const k = n.k.map((c) => {
    const x = foldLayer(c, table, fractions, dec);
    count += x.count;
    return x.ast;
  });
  return { ast: count ? mk(n.t, k, n.name) : n, count };
}

// Fraction réduite en gardant l'écriture décimale (« 3,40 » est le nombre 3,4 écrit au dixième)
function reduceQ(n) {
  if (n.q === undefined) return n;
  const g = gcd(Math.abs(n.p), n.q) || 1;
  const r = num(n.p / g, n.q / g);
  return n.dec ? { ...r, dec: n.dec } : r;
}

// Écriture décimale du résultat d'un calcul sur des nombres (au plus 6 décimales), si elle diffère de la fraction.
function decimalOf(n, r, table) {
  const R = roles(table);
  if (isLeaf(n) || n.t === R.neg || !n.k.every((c) => isLit(c, R))) return null;
  const v = exactValue(n, table);
  if (!v || v.q === 1) return null;
  const d = decNum(v);
  if (!d || d.dec.split(',')[1]?.length > 6) return null;
  return r && r.dec === d.dec ? null : d;
}

export function ratAst(v, table) {
  const R = roles(table);
  if (v.q === 1 || !R.div) return num(v.p, v.q);
  return light(mk(R.div, [num(v.p, 1), num(v.q, 1)]), table);
}
// Littéral rationnel : nombre, fraction d'entiers, ou son opposé.
function isLit(n, R) {
  if (n.t === 'Num') return true;
  if (n.t === R.neg) return isLit(n.k[0], R);
  return (
    n.t === R.div && n.k[0].t === 'Num' && n.k[1].t === 'Num' && n.k[0].q === 1 && n.k[1].q === 1
  );
}
// Un pas de « Calculer » sur le nœud n (déjà léger) → remplaçant ou null. UNE opération à la fois :
// le nœud n'a que des littéraux pour enfants (ou, s'il est assoc+comm, au moins deux nombres).
// Une somme de fractions n'est pas « un calcul » (c'est le travail des règles de fractions) ;
// une fraction d'entiers se simplifie (10/15 → 2/3, 16/8 → 2) ; irréductible, elle est déjà finale.
function foldNode(n, table, fractions = false) {
  if (isLeaf(n)) return null;
  const R = roles(table);
  if (n.t === R.neg) return null;
  if (n.t === R.div && isLit(n, R)) {
    const [a, b] = n.k;
    if (b.p === 0) return null;
    if (b.p !== 1 && gcd(Math.abs(a.p), Math.abs(b.p)) === 1) return null;
    const v = exactValue(n, table);
    return v ? ratAst(v, table) : null;
  }
  // à partir du niveau donné par le pack (options.fractionSumsFrom), « 1 − 1/6 → 5/6 » est un simple calcul
  const litOk = (c) => (n.t === R.add && !fractions ? c.t === 'Num' : isLit(c, R));
  if (n.k.every(litOk)) {
    const v = exactValue(n, table);
    if (!v) return null;
    const r = hasDecLit(n) ? (decNum(v) ?? ratAst(v, table)) : ratAst(v, table);
    return keyOf(r) === keyOf(n) ? null : r;
  }
  if (isAC(table, n.t)) {
    const nums = n.k.filter((c) => c.t === 'Num');
    const others = n.k.filter((c) => c.t !== 'Num');
    const u = unitOf(table, n.t);
    if (nums.length >= 2) {
      const v = exactValue(mk(n.t, nums, n.name), table);
      if (!v) return null;
      const kk = [...others];
      if (!(u && v.p === u.p && v.q === u.q)) kk.push(ratAst(v, table));
      return kk.length === 1
        ? kk[0]
        : kk.length === 0
          ? (u ?? ratAst(v, table))
          : mk(n.t, kk, n.name);
    }
    if (u && n.k.length >= 2 && nums.some((c) => c.p === u.p && c.q === u.q)) {
      const kk = n.k.filter((c) => !(c.t === 'Num' && c.p === u.p && c.q === u.q));
      return kk.length === 1 ? kk[0] : kk.length === 0 ? u : mk(n.t, kk, n.name);
    }
  }
  return null;
}
// Tous les calculs à la fois (de bas en haut) — sauf simplifier une fraction dont on vient de calculer
// les termes : (2 × 5)/(3 × 5) → 10/15, pas 2/3.
// Règles lues à l'envers (repli du juge) : on cherche, dans la ligne visée, le RÉSULTAT d'une règle (son rhs) ;
// on reconstruit l'écriture d'avant la règle (son lhs) → [{ rule, ast }] (ast = ligne visée avec ce morceau
// remplacé par l'écriture d'avant). Le juge garde celles qui ne diffèrent d'une ligne gardée que par des calculs.
export function rulePreimages(targetL, engine, K, limit = 400) {
  const table = engine.table,
    out = [];
  for (const rule of engine.rules) {
    if (rule.kind !== 'rewrite' || !rule.lhs || !rule.rhs) continue;
    const P = prepareRule(rule, table);
    if (!P.rhs || P.rhs.t === 'Var' || P.free.length) continue;
    visit(targetL, (n, path) => {
      // un morceau avec une lettre seulement : sur les nombres seuls, « tout calculé » confondrait les calculs
      if (out.length >= limit || isLeaf(n) || n.t !== P.rhs.t || !hasLetter(n, table)) return;
      for (const b of match(P.rhs, n, table)) {
        if (b.$rest) continue;
        const vars = new Set();
        visit(P.lhs, (x) => {
          if (x.t === 'Var') vars.add(x.name);
        });
        if ([...vars].some((v) => !b[v])) continue;
        if (rule.guard) {
          let ok = false;
          try {
            ok = rule.guard(b, K);
          } catch {
            ok = false;
          }
          if (!ok) continue;
        }
        out.push({ rule, ast: light(setAt(targetL, [...path], instantiate(P.lhs, b)), table) });
        if (out.length >= limit) return;
      }
    });
  }
  return out;
}

const hasLetter = (n, table) => [...symbols(n)].some((x) => !table.syntax.constants?.[x]);

/** Clé « tout calculé, termes semblables réunis » : deux écritures qui ne diffèrent que par des calculs et des
 * regroupements de termes ont la même. */
export function calcCollectKey(L, table) {
  return keyOf(calcNorm(collectAll(calcNorm(L, table), table), table));
}

// Forme « tout calculé » d'une écriture : les calculs sur les nombres ET sur les monômes faits partout (« x × x →
// x² », « (3x)² → 9x² », « 2 × 3x × 2 → 12x »), mais jamais les termes d'une somme réunis ni rien de développé ou
// de factorisé. Deux écritures de même forme « tout calculé » ne diffèrent que par des calculs.
const CALC = new WeakMap();
export function calcNorm(L, table) {
  let cache = CALC.get(table);
  if (!cache) {
    cache = new Map();
    CALC.set(table, cache);
  }
  const key = keyOf(L);
  const hit = cache.get(key);
  if (hit) return hit;
  const R = roles(table);
  const go = (n) => {
    if (isLeaf(n)) return n;
    let x = light(mk(n.t, n.k.map(go), n.name), table);
    x = foldAll(x, table);
    // 0 divisé par un nombre non nul écrit sans lettre (« 0 ÷ (√6 × √11) ») : 0
    if (
      !isLeaf(x) &&
      x.t === R.div &&
      x.k[0].t === 'Num' &&
      x.k[0].p === 0 &&
      !hasLetterSym(x.k[1], table)
    ) {
      let d = NaN;
      try {
        d = Number(evaluate(x.k[1], {}, table));
      } catch {
        d = NaN;
      }
      if (Number.isFinite(d) && d !== 0) return num(0);
    }
    if (!isLeaf(x) && x.t === R.mul) x = monoProduct(x.k, table, R) ?? x;
    else if (
      !isLeaf(x) &&
      x.t === R.div &&
      x.k[1].t === 'Num' &&
      x.k[1].p !== 0 &&
      hasLetterSym(x.k[0], table)
    ) {
      // monôme divisé par un nombre : « 12h ÷ 2 → 6h », « 0,3a ÷ 0,4 → 0,75a »
      const inv = num(x.k[1].q * Math.sign(x.k[1].p), Math.abs(x.k[1].p));
      const top = !isLeaf(x.k[0]) && x.k[0].t === R.mul ? x.k[0].k : [x.k[0]];
      x = monoProduct([...top, inv], table, R, true) ?? x;
    } else if (
      !isLeaf(x) &&
      x.t === R.div &&
      hasLetterSym(x.k[0], table) &&
      letterPower(x.k[1], table, R)
    ) {
      // monôme divisé par une puissance de lettre : « x² ÷ x → x », « 3x³ ÷ x² → 3x »
      const [name, e] = letterPower(x.k[1], table, R);
      const top = !isLeaf(x.k[0]) && x.k[0].t === R.mul ? x.k[0].k : [x.k[0]];
      x = monoProduct([...top, mk(R.pow, [{ t: 'Sym', name }, num(-e)])], table, R, true) ?? x;
    } else if (
      !isLeaf(x) &&
      x.t === R.pow &&
      isIntNum(x.k[1]) &&
      !isLeaf(x.k[0]) &&
      (x.k[0].t === R.mul || x.k[0].t === R.neg)
    ) {
      // puissance d'un monôme : chaque facteur élevé à la puissance (« (−2x)² → 4x² »)
      const base =
        x.k[0].t === R.neg
          ? [num(-1), ...(x.k[0].k[0].t === R.mul ? x.k[0].k[0].k : [x.k[0].k[0]])]
          : x.k[0].k;
      x =
        monoProduct(
          base.map((c) => foldAll(light(mk(R.pow, [c, x.k[1]]), table), table)),
          table,
          R,
          true,
        ) ?? x;
    }
    if (!isLeaf(x)) {
      // multiple rationnel d'une constante (« 7π/5 − 2π », « (π ÷ 6) × 36 ») : coefficient calculé
      const lc = constMultiple(x, table, R);
      if (lc && lc.name)
        return lc.c.p === 1 && lc.c.q === 1
          ? { t: 'Sym', name: lc.name }
          : mk(R.mul, [num(lc.c.p, lc.c.q), { t: 'Sym', name: lc.name }]);
    }
    return isLeaf(x) ? x : foldAll(x, table);
  };
  const out = go(L);
  if (cache.size > 50000) cache.clear();
  cache.set(key, out);
  return out;
}
// Écriture « c × K » (c rationnel, K une constante de valeur irrationnelle comme π) → { c, name }, un nombre →
// { c, name: null } ; null sinon (deux constantes différentes, une lettre, une fonction…)
function constMultiple(n, table, R) {
  if (n.t === 'Num') return { c: n, name: null };
  if (n.t === 'Sym') {
    const v = constValue(table.syntax.constants?.[n.name]);
    return Number.isFinite(v) && !Number.isInteger(v) ? { c: num(1), name: n.name } : null;
  }
  if (isLeaf(n)) return null;
  const parts = n.k.map((c) => constMultiple(c, table, R));
  if (parts.some((p) => !p)) return null;
  const names = [...new Set(parts.map((p) => p.name).filter(Boolean))];
  if (names.length > 1) return null;
  const name = names[0] ?? null;
  if (n.t === R.neg) return { c: Q.neg(parts[0].c), name };
  if (n.t === R.add) {
    // a + bπ ne se réduit pas : tous les termes portent la constante (ou aucun)
    if (name && parts.some((p) => !p.name)) return null;
    return { c: parts.reduce((s0, p) => Q.add(s0, p.c), num(0)), name };
  }
  if (n.t === R.mul) {
    if (parts.filter((p) => p.name).length > 1) return null;
    return { c: parts.reduce((s0, p) => Q.mul(s0, p.c), num(1)), name };
  }
  if (n.t === R.div) {
    if (parts[1].name || parts[1].c.p === 0) return null;
    return { c: Q.div(parts[0].c, parts[1].c), name };
  }
  return null;
}
const isIntNum = (n) => n.t === 'Num' && n.q === 1 && n.p >= 2 && n.p <= 12;
// « x » → ['x', 1], « x² » → ['x', 2] (lettre qui n'est pas une constante), sinon null
const letterPower = (n, table, R) => {
  if (n.t === 'Sym' && !table.syntax.constants?.[n.name]) return [n.name, 1];
  if (
    !isLeaf(n) &&
    n.t === R.pow &&
    n.k[0].t === 'Sym' &&
    !table.syntax.constants?.[n.k[0].name] &&
    n.k[1].t === 'Num' &&
    n.k[1].q === 1 &&
    n.k[1].p > 0
  )
    return [n.k[0].name, n.k[1].p];
  return null;
};
const hasLetterSym = (n, table) => [...symbols(n)].some((x) => !table.syntax.constants?.[x]);

// Produit de facteurs → coefficient × puissances de lettres × autres facteurs (null si rien ne change)
function monoProduct(factors, table, R, force = false) {
  let coef = num(1),
    changed = force;
  const pows = new Map(),
    others = [];
  const addPow = (name, e) => {
    if (pows.has(name)) changed = true;
    pows.set(name, (pows.get(name) ?? 0) + e);
  };
  for (const f0 of factors) {
    let f = f0;
    if (!isLeaf(f) && f.t === R.neg) {
      coef = Q.mul(coef, num(-1));
      f = f.k[0];
      changed = true;
    }
    if (f.t === 'Num') {
      if (coef.p !== 1 || coef.q !== 1) changed = true;
      coef = Q.mul(coef, f);
      continue;
    }
    if (f.t === 'Sym' && !table.syntax.constants?.[f.name]) {
      addPow(f.name, 1);
      continue;
    }
    if (
      !isLeaf(f) &&
      f.t === R.pow &&
      f.k[0].t === 'Sym' &&
      !table.syntax.constants?.[f.k[0].name] &&
      f.k[1].t === 'Num' &&
      f.k[1].q === 1
    ) {
      addPow(f.k[0].name, f.k[1].p);
      continue;
    }
    // (xᵃ)ᵇ → x^(a × b)
    if (
      !isLeaf(f) &&
      f.t === R.pow &&
      !isLeaf(f.k[0]) &&
      f.k[0].t === R.pow &&
      f.k[0].k[0].t === 'Sym' &&
      !table.syntax.constants?.[f.k[0].k[0].name] &&
      f.k[0].k[1].t === 'Num' &&
      f.k[0].k[1].q === 1 &&
      f.k[1].t === 'Num' &&
      f.k[1].q === 1
    ) {
      changed = true;
      addPow(f.k[0].k[0].name, f.k[0].k[1].p * f.k[1].p);
      continue;
    }
    if (!isLeaf(f) && f.t === R.mul) {
      changed = true;
      for (const g of f.k) others.push(g);
      continue;
    }
    others.push(f);
  }
  if (!changed) return null;
  const parts = [];
  if (coef.p !== 1 || coef.q !== 1 || (!pows.size && !others.length)) parts.push(coef);
  for (const [name, e] of [...pows].sort((a, b) => (a[0] < b[0] ? -1 : 1))) {
    if (e === 0) continue;
    parts.push(e === 1 ? { t: 'Sym', name } : mk(R.pow, [{ t: 'Sym', name }, num(e)]));
  }
  parts.push(...others);
  if (!parts.length) return num(1);
  return light(parts.length === 1 ? parts[0] : mk(R.mul, parts), table);
}

export function foldAll(n, table) {
  if (isLeaf(n)) return n;
  const k = n.k.map((c) => foldAll(c, table));
  const changed = !k.every((c, i) => c === n.k[i]);
  const x = light(changed ? mk(n.t, k, n.name) : n, table);
  if (changed && x.t === roles(table).div) return x;
  let r = foldNode(x, table);
  // un opposé dont le contenu se calcule : −(0,125 × 4) → −0,5
  if (!r && x.t === roles(table).neg && !isLeaf(x.k[0]) && x.k[0].t !== roles(table).div) {
    const c = foldNode(x.k[0], table);
    if (c) r = mk(x.t, [c]);
  }
  return r ? light(r, table) : x;
}

// Termes semblables : décomposition coefficient × monôme d'un terme de somme
function decompose(t, table, R) {
  if (t.t === 'Num') return { c: num(t.p, t.q), mono: null };
  if (t.t === R.neg) {
    const d = decompose(t.k[0], table, R);
    return { c: Q.neg(d.c), mono: d.mono };
  }
  if (t.t === R.mul) {
    const nums = t.k.filter((c) => c.t === 'Num'),
      others = t.k.filter((c) => c.t !== 'Num');
    let c = num(1);
    for (const x of nums) c = Q.mul(c, x);
    const mono =
      others.length === 0
        ? null
        : others.length === 1
          ? others[0]
          : light(mk(R.mul, others), table);
    return { c, mono };
  }
  return { c: num(1), mono: t };
}
// coefficient écrit en décimal si les termes regroupés l'étaient (ℓ − 0,5ℓ → 0,5ℓ, pas 1/2 × ℓ)
function termOf(c, mono, table, R, dec = false) {
  const coef = (v) => (dec ? (decNum(v) ?? ratAst(v, table)) : ratAst(v, table));
  if (!mono) return coef(c);
  if (c.p === c.q) return mono;
  if (c.p === -c.q) return light(mk(R.neg, [mono]), table);
  return light(mk(R.mul, [coef(c), mono]), table);
}
// Variantes de « Réduire » sur un nœud : une par groupe, plus « tout à la fois » (clé 'all').
function collectNode(n, table, R) {
  const variants = [];
  if (n.t === R.add) {
    const groups = new Map();
    n.k.forEach((t, i) => {
      const d = decompose(t, table, R);
      const key = d.mono ? keyOf(d.mono) : '#';
      if (!groups.has(key)) groups.set(key, { idx: [], c: num(0), mono: d.mono, dec: false });
      const g = groups.get(key);
      g.idx.push(i);
      g.c = Q.add(g.c, d.c);
      g.dec ||= hasDecLit(t);
    });
    const multi = [...groups.values()].filter((g) => g.idx.length >= 2);
    if (!multi.length) return variants;
    const build = (sel) => {
      const kk = [];
      n.k.forEach((t, i) => {
        const g = sel.find((s) => s.idx.includes(i));
        if (!g) kk.push(t);
        else if (g.idx[0] === i && g.c.p !== 0) kk.push(termOf(g.c, g.mono, table, R, g.dec));
      });
      return kk.length === 0
        ? (unitOf(table, n.t) ?? num(0))
        : kk.length === 1
          ? kk[0]
          : mk(n.t, kk, n.name);
    };
    for (const g of multi) variants.push(build([g]));
    if (multi.length > 1) variants.push(build(multi));
  } else if (n.t === R.mul && R.pow) {
    const groups = new Map(),
      nums = [];
    n.k.forEach((f, i) => {
      if (f.t === 'Num') {
        nums.push(i);
        return;
      }
      let base = f,
        e = num(1);
      if (f.t === R.pow && f.k[1].t === 'Num') {
        base = f.k[0];
        e = f.k[1];
      }
      const key = keyOf(base);
      if (!groups.has(key)) groups.set(key, { idx: [], e: num(0), base });
      const g = groups.get(key);
      g.idx.push(i);
      g.e = Q.add(g.e, e);
    });
    const multi = [...groups.values()].filter((g) => g.idx.length >= 2);
    if (!multi.length && nums.length < 2) return variants;
    const build = (sel, withNums) => {
      const kk = [];
      let c = num(1);
      n.k.forEach((f, i) => {
        if (withNums && nums.includes(i)) {
          c = Q.mul(c, f);
          return;
        }
        const g = sel.find((s) => s.idx.includes(i));
        if (!g) kk.push(f);
        else if (g.idx[0] === i && g.e.p !== 0)
          kk.push(g.e.p === g.e.q ? g.base : mk(R.pow, [g.base, num(g.e.p, g.e.q)]));
      });
      if (withNums && !(c.p === 1 && c.q === 1)) kk.unshift(num(c.p, c.q));
      return kk.length === 0 ? num(c.p, c.q) : kk.length === 1 ? kk[0] : mk(n.t, kk, n.name);
    };
    for (const g of multi) variants.push(build([g], false));
    if (multi.length > 1 || nums.length >= 2) variants.push(build(multi, nums.length >= 2));
  }
  return variants.map((v) => light(v, table));
}
export function collectAll(n, table) {
  if (isLeaf(n)) return n;
  const R = roles(table);
  const k = n.k.map((c) => collectAll(c, table));
  const x = light(k.every((c, i) => c === n.k[i]) ? n : mk(n.t, k, n.name), table);
  const v = collectNode(x, table, R);
  return v.length ? v[v.length - 1] : x;
}
export const tidy = (n, table) => foldAll(collectAll(n, table), table);

// Mesures de « dette de calcul » d'une expression (pour l'utilité et la forme finale)
export function features(n, table) {
  const R = roles(table);
  const f = { size: 0, nonExpanded: 0, foldable: 0, collectable: 0 };
  const go = (x) => {
    f.size++;
    if (isLeaf(x)) return;
    if (foldNode(x, table)) f.foldable++;
    if ((x.t === R.add || x.t === R.mul) && collectNode(x, table, R).length) f.collectable++;
    if (x.t === R.mul && x.k.some((c) => c.t === R.add)) f.nonExpanded++;
    if (x.t === R.neg && x.k[0].t === R.add) f.nonExpanded++;
    if (
      x.t === R.pow &&
      x.k[0].t === R.add &&
      x.k[1].t === 'Num' &&
      x.k[1].q === 1 &&
      x.k[1].p >= 2
    )
      f.nonExpanded++;
    x.k.forEach(go);
  };
  go(n);
  return f;
}

// ── Gestes builtin (stratégies génériques) ────────────────────────────────
// Signature : (L léger, table, K) → AST légers. K porte les données du modèle : target, given
// ({ nom: AST valeur }), preds ([{ L }]), pred (prédicats).
function eachPos(L, f) {
  visit(L, (n, path) => {
    if (!isLeaf(n)) f(n, path);
  });
}
const relSpec = (table, n) => {
  const s = table.nodes[n.t];
  return s && s.relation ? s : null;
};

const isConstLit = (n, table) => [...symbols(n)].every((s) => table.syntax.constants?.[s]);
export const opsCount = (n) => (isLeaf(n) ? 0 : 1 + n.k.reduce((s, c) => s + opsCount(c), 0));
function litValue(n, table) {
  const v = evaluate(n, {}, table);
  if (typeof v === 'number') return v;
  return v && v.q !== undefined ? Number(v.p) / Number(v.q) : NaN;
}

// plus grand k > 1 tel que k² divise n (entier positif), ou null
function squareFactor(n) {
  if (!Number.isInteger(n) || n < 4 || n > 1e7) return null;
  for (let k = Math.floor(Math.sqrt(n)); k > 1; k--) if (n % (k * k) === 0) return k;
  return null;
}
// les racines d'un entier qui contiennent un carré : position, k, reste m (√n = k√m)
function rootsWithSquare(L, table) {
  const R = roles(table),
    out = [];
  eachPos(L, (n, path) => {
    if (n.t !== R.sqrt || !isConstLit(n.k[0], table)) return;
    const v = litValue(n.k[0], table),
      k = squareFactor(Math.round(v));
    if (k && Math.abs(v - Math.round(v)) < 1e-12)
      out.push({ n, path: [...path], k, m: Math.round(v) / (k * k) });
  });
  return out;
}

// P − Q quand c'est un entier constant (« (n + 1) − n = 1 », « 100 − 98 = 2 »), sinon null
export function intGap(P, Q, table) {
  const vars = [...symbols(P), ...symbols(Q)].filter((v) => !table.syntax.constants?.[v]);
  let d = null;
  for (const base of [3, 7, 11]) {
    const env = {};
    vars.forEach((v, j) => (env[v] = num(base + 5 * j)));
    const a = evaluate(P, env, table),
      b = evaluate(Q, env, table);
    const x = typeof a === 'number' ? a : Number(a?.p) / Number(a?.q),
      y = typeof b === 'number' ? b : Number(b?.p) / Number(b?.q);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    const g = x - y;
    if (Math.abs(g - Math.round(g)) > 1e-9 || (d !== null && Math.round(g) !== d)) return null;
    d = Math.round(g);
  }
  return d;
}

export const STRATEGIES = {
  // Reconnaître k parmi n : « n! ÷ (k! × (n − k)!) » → « k parmi n » (les deux factorielles du bas font n)
  parmi_read(L, table) {
    const R = roles(table),
      out = [];
    const fact = Object.keys(table.nodes).find((t) => table.nodes[t].eval === 'fact'),
      parmi = Object.keys(table.nodes).find((t) => table.nodes[t].eval === 'parmi');
    if (!fact || !parmi) return out;
    const factors = (n) => (!isLeaf(n) && n.t === R.mul ? n.k : [n]);
    const prod = (ks) => (ks.length === 1 ? ks[0] : mk(R.mul, ks));
    eachPos(L, (n, path) => {
      if (n.t !== R.div) return;
      const top = factors(n.k[0]),
        bot = factors(n.k[1]);
      const i = top.findIndex((f) => f.t === fact);
      if (i < 0) return;
      for (let a = 0; a < bot.length; a++)
        for (let b = 0; b < bot.length; b++) {
          if (a === b || bot[a].t !== fact || bot[b].t !== fact) continue;
          const N = top[i].k[0],
            K = bot[a].k[0];
          if (intGap(N, mk(R.add, [K, bot[b].k[0]]), table) !== 0) continue;
          const rest = bot.filter((_, x) => x !== a && x !== b);
          const T = [...top];
          T.splice(i, 1, mk(parmi, [K, N]));
          const v = rest.length ? mk(R.div, [prod(T), prod(rest)]) : prod(T);
          out.push(light(setAt(L, path, v), table));
        }
    });
    return out;
  },
  // Simplifier les factorielles d'un quotient : « 100! ÷ 98! → 100 × 99 », « (n + 1)! ÷ n! → n + 1 »,
  // « k × n! ÷ (k! × …) → n! ÷ ((k − 1)! × …) » (k ÷ k! = 1 ÷ (k − 1)!)
  fact_quotient(L, table) {
    const R = roles(table),
      out = [];
    const fact = Object.keys(table.nodes).find((t) => table.nodes[t].eval === 'fact');
    if (!fact) return out;
    const factors = (n) => (!isLeaf(n) && n.t === R.mul ? n.k : [n]);
    const prod = (ks) => (ks.length === 0 ? num(1) : ks.length === 1 ? ks[0] : mk(R.mul, ks));
    const sub = (a, c) => mk(R.add, [a, num(-c)]);
    eachPos(L, (n, path) => {
      if (n.t !== R.div) return;
      const top = factors(n.k[0]),
        bot = factors(n.k[1]);
      for (let i = 0; i < top.length; i++)
        for (let j = 0; j < bot.length; j++) {
          const a = top[i],
            b = bot[j];
          let T = null,
            B = null;
          if (a.t === fact && b.t === fact) {
            const d = intGap(a.k[0], b.k[0], table);
            if (d === null || d === 0 || Math.abs(d) > 6) continue;
            const [hi, lo, up] = d > 0 ? [a.k[0], b.k[0], true] : [b.k[0], a.k[0], false];
            const terms = [hi];
            for (let c = 1; c < Math.abs(d); c++) terms.push(sub(hi, c));
            T = [...top];
            B = [...bot];
            if (up) {
              T.splice(i, 1, ...terms);
              B.splice(j, 1);
            } else {
              T.splice(i, 1);
              B.splice(j, 1, ...terms);
            }
            void lo;
          } else if (b.t === fact && a.t !== fact && intGap(a, b.k[0], table) === 0) {
            // k ÷ k! = 1 ÷ (k − 1)!
            T = top.filter((_, x) => x !== i);
            B = [...bot];
            B.splice(j, 1, mk(fact, [sub(b.k[0], 1)]));
          } else continue;
          const D = prod(B);
          const v = B.length === 0 ? prod(T) : mk(R.div, [prod(T), D]);
          out.push(light(setAt(L, path, v), table));
        }
    });
    return out;
  },
  // Écrire la factorielle d'un ENTIER écrit : « 8! → 8 × 7! », « 10! → 10 × 9 × 8! » (un à trois facteurs sortis) —
  // pour faire apparaître la factorielle du bas d'un quotient (la règle du pack n'agit que sur les lettres)
  fact_expand(L, table) {
    const R = roles(table),
      out = [];
    const fact = Object.keys(table.nodes).find((t) => table.nodes[t].eval === 'fact');
    if (!fact) return out;
    eachPos(L, (n, path) => {
      if (n.t !== fact || n.k[0].t !== 'Num' || n.k[0].q !== 1) return;
      const v = Number(n.k[0].p);
      if (!Number.isInteger(v) || v < 2 || v > 30) return;
      for (let c = 1; c <= 3 && v - c >= 1; c++) {
        const ks = [];
        for (let j = 0; j < c; j++) ks.push(num(v - j));
        ks.push(mk(fact, [num(v - c)]));
        out.push(light(setAt(L, path, mk(R.mul, ks)), table));
      }
    });
    return out;
  },
  // Faire apparaître un carré sous la racine : « √12 → √(4 × 3) »
  sqrt_factor(L, table) {
    const R = roles(table);
    return rootsWithSquare(L, table)
      .filter((r) => r.n.k[0].t === 'Num')
      .map((r) =>
        light(setAt(L, r.path, mk(R.sqrt, [mk(R.mul, [num(r.k * r.k), num(r.m)])])), table),
      );
  },
  // Simplifier la racine : « √12 → 2√3 », « √(4 × 3) → 2√3 » (« √36 → 6 » est un calcul)
  sqrt_simplify(L, table) {
    const R = roles(table),
      out = [];
    const all = rootsWithSquare(L, table).filter((r) => r.m > 1);
    for (const r of all)
      out.push(light(setAt(L, r.path, mk(R.mul, [num(r.k), mk(R.sqrt, [num(r.m)])])), table));
    if (all.length > 1) {
      let A = L;
      for (const r of all) A = setAt(A, r.path, mk(R.mul, [num(r.k), mk(R.sqrt, [num(r.m)])]));
      out.push(light(A, table));
    }
    return out;
  },
  // Calculer : un calcul numérique sur un sous-terme (et la variante « tous les calculs à la fois »)
  fold(L, table, K) {
    const out = [];
    const fr = !!K?.foldFractions;
    eachPos(L, (n, path) => {
      const r = foldNode(n, table, fr);
      if (r) out.push(light(setAt(L, path, r), table));
      // le même calcul écrit en décimal (« 45 ÷ 60 → 0,75 », « 3 ÷ 4 → 0,75 ») quand le résultat est un décimal
      // court : c'est ainsi que l'écrivent la plupart des élèves (lot M1, feuilles « formule » publiées)
      const d = decimalOf(n, r, table);
      if (d) out.push(light(setAt(L, path, d), table));
      // UNE opération à la fois dans une somme / un produit de trois nombres ou plus : deux nombres voisins
      // (« 0,6 × 0,6 × 0,6 → 0,36 × 0,6 », « 1 + 4 + 9 → 5 + 9 »)
      for (const p of pairFolds(n, table, fr)) out.push(light(setAt(L, path, p), table));
    });
    // UN calcul à chacun de plusieurs endroits d'une même ligne (« (10³)² × 10⁻⁴ → 1000² × 0,0001 ») : chaque
    // calcul fait est le plus intérieur de son endroit, jamais deux opérations enchaînées au même endroit
    for (const dec of [false, true]) {
      const layer = foldLayer(L, table, fr, dec);
      if (layer.count >= 2) out.push(light(layer.ast, table));
    }
    const all = foldAll(L, table);
    if (all !== L) out.push(all);
    return out;
  },
  // Arrondir : « R = 2√3 » → « R ≈ 3,46 » (de l'unité au dix-millième). Le membre de droite doit avoir une
  // valeur (nombres, constantes, grandeurs connues de l'exercice) ; le juge garde la précision qu'a écrite l'élève.
  round(L, table, K) {
    if (!K?.rounding) return [];
    const rp = table.nodes[L.t];
    if (!rp?.relation || rp.relation !== 'eq' || L.k.length !== 2) return [];
    const approx = Object.entries(table.nodes).find(([, sp]) => sp.relation === 'approx')?.[0];
    if (!approx) return [];
    // angle en degrés (feuille « formule », K.degreeWorld) : arccos(c) se lit en degrés
    const R = K?.degreeWorld ? K.degreeWorld(L.k[1]) : L.k[1];
    const v = evaluate(R, { ...(K?.world ?? {}) }, table);
    const x = typeof v === 'number' ? v : v && v.q !== undefined ? Number(v.p) / Number(v.q) : NaN;
    if (!Number.isFinite(x)) return [];
    const out = [];
    for (let d = 0; d <= 4; d++) {
      const m = Math.round(Math.abs(x) * 10 ** d) * Math.sign(x);
      const n = decNum({ p: m, q: 10 ** d }) ?? num(m, 10 ** d);
      out.push(light(mk(approx, [L.k[0], reduceQ(n)]), table));
    }
    return out;
  },
  // Écrire autrement (feuilles d'affirmations) : remplacer un nombre ou un calcul sur des nombres par une écriture
  // de MÊME valeur et plus détaillée, prise dans la ligne visée (K.unfoldTarget) : « 3 → √9 », « 12 → 3 × 4 »,
  // « 97 → 3 × 32 + 1 », « 10⁻⁴ → 1 ÷ 10⁴ », « 3π ÷ 4 → π − π ÷ 4 ». Jamais un calcul fait (l'écriture s'allonge).
  unfold(L, table, K) {
    const T = K?.unfoldTarget;
    if (!T) return [];
    const targets = [];
    visit(T, (n) => {
      if (isLeaf(n) || !isConstLit(n, table)) return;
      const v = litValue(n, table);
      if (Number.isFinite(v) && targets.length < 16)
        targets.push({ n, v, ops: opsCount(n), key: keyOf(n) });
    });
    if (!targets.length) return [];
    const out = [];
    visit(L, (n, path) => {
      if (n.t === 'Var' || !isConstLit(n, table)) return;
      const v = litValue(n, table),
        ops = opsCount(n),
        key = keyOf(n);
      if (!Number.isFinite(v)) return;
      for (const t of targets) {
        if (t.ops <= ops || t.key === key) continue;
        if (Math.abs(t.v - v) > 1e-9 * Math.max(1, Math.abs(v))) continue;
        out.push(light(setAt(L, path, t.n), table));
      }
    });
    return out;
  },
  // Le plus petit / plus grand entier : « n > 10,3 » → « n ≥ 11 » quand l'inconnue est un entier (hypothèse
  // natural / integer du modèle)
  integer_bound(L, table, K) {
    const n = K?.target;
    if (
      !n ||
      !(K.hyps ?? []).some((h) => h.of === n && (h.kind === 'natural' || h.kind === 'integer'))
    )
      return [];
    const rel = table.nodes[L.t]?.relation;
    if (!['lt', 'le', 'gt', 'ge'].includes(rel) || L.k.length !== 2) return [];
    let [a, b] = L.k,
      op = rel;
    const flip = { lt: 'gt', le: 'ge', gt: 'lt', ge: 'le' };
    if (!(a.t === 'Sym' && a.name === n)) {
      if (!(b.t === 'Sym' && b.name === n)) return [];
      [a, b] = [b, a];
      op = flip[op];
    }
    if (symbols(b).has(n)) return [];
    const x = litValue(b, table);
    if (!Number.isFinite(x)) return [];
    const relT = (r) => Object.entries(table.nodes).find(([, sp]) => sp.relation === r)?.[0];
    const fl = Math.floor(x + 1e-12),
      ce = Math.ceil(x - 1e-12);
    const bound =
      op === 'gt'
        ? ['ge', Math.floor(x) + 1]
        : op === 'ge'
          ? ['ge', ce]
          : op === 'lt'
            ? ['le', Math.ceil(x) - 1]
            : ['le', fl];
    const t = relT(bound[0]);
    if (!t || (b.t === 'Num' && b.q === 1)) return [];
    return [light(mk(t, [a, num(bound[1])]), table)];
  },
  // Réduire : regrouper des termes semblables (somme) ou des facteurs de même base (produit)
  collect(L, table) {
    const R = roles(table),
      out = [];
    eachPos(L, (n, path) => {
      for (const v of collectNode(n, table, R)) out.push(light(setAt(L, path, v), table));
    });
    const all = collectAll(L, table);
    if (all !== L) out.push(all);
    return out;
  },
  // Remplacer une grandeur connue (symbols[].given) par sa valeur : une à une, ou toutes
  substitute(L, table, K) {
    const given = K.given || {};
    const alt = K.givenAlt || {};
    const altOf = (n) => (Array.isArray(alt[n]) ? alt[n] : alt[n] ? [alt[n]] : []);
    // une grandeur sans valeur donnée mais à valeur établie (`givenAlt`) se remplace aussi
    const names = [...symbols(L)].filter((s) => given[s] || altOf(s).length);
    let useAlt = -1;
    const rep = (x, set) =>
      x.t === 'Sym' && set.has(x.name)
        ? (useAlt >= 0 && altOf(x.name)[useAlt]) || given[x.name] || x
        : isLeaf(x)
          ? x
          : mk(
              x.t,
              x.k.map((c) => rep(c, set)),
              x.name,
            );
    // « toutes à la fois » : sauf la grandeur seule d'un membre (« y = c × p + d × q » garde son y)
    const heads = new Set(
      table.nodes[L.t]?.relation ? L.k.filter((m) => m.t === 'Sym').map((m) => m.name) : [],
    );
    const all = names.filter((s) => !heads.has(s));
    const variants = () => {
      const out = names.map((s) => light(rep(L, new Set([s])), table));
      if (all.length > 1) out.push(light(rep(L, new Set(all)), table));
      if (names.length > 1 && all.length !== names.length)
        out.push(light(rep(L, new Set(names)), table));
      return out;
    };
    const out = variants();
    // les mêmes, avec l'écriture décimale d'une valeur exacte qui tombe juste (« 35 ÷ 100 » → 0,35)
    const nAlt = Math.max(0, ...names.map((n) => altOf(n).length));
    for (let i = 0; i < nAlt; i++) {
      useAlt = i;
      out.push(...variants());
    }
    return out;
  },
  // Relation de Chasles : XY⃗ + YZ⃗ → XZ⃗ (deux termes d'une même somme, dans n'importe quel ordre) ; XX⃗ → 0⃗
  chasles(L, table) {
    const R = roles(table),
      out = [];
    const m = table.lex?.vectorMark;
    if (!m || !R.add) return out;
    const vec = (a, b) => ({ t: 'Sym', name: (a === b ? '0' : a + b) + m });
    eachPos(L, (n, path) => {
      if (n.t !== R.add) return;
      const pts = n.k.map((t) => (t.t === 'Sym' ? vectorPoints(t.name, table) : null));
      for (let i = 0; i < n.k.length; i++)
        for (let j = 0; j < n.k.length; j++) {
          if (i === j || !pts[i]?.length || !pts[j]?.length || pts[i][1] !== pts[j][0]) continue;
          const merged = vec(pts[i][0], pts[j][1]);
          const rest = n.k.filter((_, k) => k !== i && k !== j);
          const r = rest.length ? mk(R.add, [...rest, merged]) : merged;
          out.push(light(setAt(L, path, r), table));
        }
    });
    return out;
  },
  // Vecteur opposé : −AB⃗ → BA⃗
  vector_opposite(L, table) {
    const R = roles(table),
      out = [];
    const m = table.lex?.vectorMark;
    if (!m || !R.neg) return out;
    visit(L, (n, path) => {
      if (n.t !== R.neg || n.k[0]?.t !== 'Sym') return;
      const vp = vectorPoints(n.k[0].name, table);
      if (vp?.length === 2)
        out.push(light(setAt(L, path, { t: 'Sym', name: vp[1] + vp[0] + m }), table));
    });
    return out;
  },
  // ── Analyse : dérivées ──
  // Linéarité : (a + b − 3c)′ → (a)′ + (b)′ − 3 × (c)′ (les facteurs constants sortent, la dérivée se répartit)
  derive_linear(L, table) {
    const R = roles(table),
      out = [];
    const all = mapDer(L, table, (u, v) => linearParts(u, v, table, R));
    eachPos(L, (n, path) => {
      if (table.nodes[n.t]?.eval !== 'derive') return;
      const r = linearParts(n.k[0], varOf(n.k[0], table), table, R);
      if (r) out.push(light(setAt(L, path, r), table));
    });
    if (all && out.length > 1) out.push(light(all, table));
    return out;
  },
  // Dérivées usuelles : (xⁿ)′ = nxⁿ⁻¹, (c)′ = 0, (x)′ = 1, (√x)′, (1 ÷ x)′, (eˣ)′, (ln x)′, (sin x)′, (cos x)′
  derive_ref(L, table) {
    const out = [];
    eachPos(L, (n, path) => {
      if (table.nodes[n.t]?.eval !== 'derive') return;
      const r = refDerivative(n.k[0], varOf(n.k[0], table), table);
      if (r) out.push(light(setAt(L, path, r), table));
    });
    const all = mapDer(L, table, (u, v) => refDerivative(u, v, table));
    if (all && out.length > 1) out.push(light(all, table));
    return out;
  },
  // ── Analyse : limites ──
  // lim(a + b) → lim(a) + lim(b), lim(a × b) → lim(a) × lim(b), lim(a ÷ b) → lim(a) ÷ lim(b), lim(k × u) → k × lim(u)
  limit_split(L, table) {
    const R = roles(table),
      out = [];
    eachPos(L, (n, path) => {
      if (!isLim(n, table)) return;
      const [u, p] = n.k;
      const v = varOf(u, table);
      const lim = (x) => (v && symbols(x).has(v) ? mk(n.t, p ? [x, p] : [x], n.name) : x);
      let r = null;
      if (u.t === R.add || u.t === R.mul) r = mk(u.t, u.k.map(lim));
      else if (u.t === R.div || u.t === R.neg) r = mk(u.t, u.k.map(lim));
      if (r && keyOf(light(r, table)) !== keyOf(n)) out.push(light(setAt(L, path, r), table));
      // jusqu'aux limites de référence d'un coup (lim(6 − 4 × 0,5ⁿ) → 6 − 4 × lim(0,5ⁿ)), sans créer de forme indéterminée
      const deep = splitDeep(u, v, p, n, table, R);
      if (deep && keyOf(light(deep, table)) !== keyOf(light(r ?? n, table)))
        out.push(light(setAt(L, path, deep), table));
    });
    return out;
  },
  // Opérations sur les limites (sans forme indéterminée) : lim(u) → sa valeur quand elle se lit sur les limites de
  // référence ; et les calculs sur l'infini : 3 × (+∞) → +∞, +∞ + 2 → +∞, 5 ÷ (+∞) → 0
  limit_ops(L, table) {
    return limitValues(L, table, false);
  },
  // Croissances comparées : eˣ ÷ x → +∞, x ÷ eˣ → 0, ln(x) ÷ x → 0, xeˣ en −∞ → 0
  limit_growth(L, table) {
    return limitValues(L, table, true);
  },
  // Factoriser par le terme dominant (sous une limite) : 3n² − 5n + 1 → n²(3 − 5 ÷ n + 1 ÷ n²)
  factor_dominant(L, table) {
    const R = roles(table),
      out = [];
    eachPos(L, (n, path) => {
      if (!isLim(n, table)) return;
      const [u, p] = n.k;
      const v = varOf(u, table);
      if (!v) return;
      const point = p ? limitPoint(p, table) : Infinity;
      if (Number.isFinite(point) || Number.isNaN(point)) return; // seulement en ±∞
      const put = (x) =>
        out.push(
          light(
            setAt(L, path, mk(n.t, p ? [light(x, table), p] : [light(x, table)], n.name)),
            table,
          ),
        );
      if (u.t === R.add) {
        const f = factorDominant(u, v, point, table, R);
        if (f) put(mk(R.mul, [f.T, f.rest]));
      }
      if (u.t === R.div) {
        const [a, b] = u.k;
        const fa = a.t === R.add ? factorDominant(a, v, point, table, R) : null;
        const fb = b.t === R.add ? factorDominant(b, v, point, table, R) : null;
        if (!fa && !fb) return;
        const A = fa ? mk(R.mul, [fa.T, fa.rest]) : a,
          B = fb ? mk(R.mul, [fb.T, fb.rest]) : b;
        put(mk(R.div, [A, B]));
        // et la simplification des puissances de la variable : (n(2 + 1 ÷ n)) ÷ (n(1 + 3 ÷ n)) → (2 + 1 ÷ n) ÷ (1 + 3 ÷ n)
        const ea = fa ? fa.mono : monoOf(a, v, R),
          eb = fb ? fb.mono : monoOf(b, v, R);
        if (ea && eb) {
          const d = Q.add(ea.a, Q.neg(eb.a));
          const ra = fa ? fa.rest : coefAst(ea.c, R),
            rb = fb ? fb.rest : coefAst(eb.c, R);
          const quo = mk(R.div, [ra, rb]);
          const pw = powerAst(v, d, R);
          put(
            pw
              ? d.p > 0
                ? mk(R.mul, [pw, quo])
                : mk(R.div, [ra, mk(R.mul, [rb, powerAst(v, Q.neg(d), R)])])
              : quo,
          );
        }
      }
    });
    return out;
  },
  // Écrire une formule du modèle (preds[].line)
  formula(L, table, K) {
    return (K.preds || []).map((p) => p.L);
  },
  // Même terme ajouté / retranché aux deux membres
  relation_add(L, table) {
    const R = roles(table),
      out = [];
    if (!R.add) return out;
    eachPos(L, (n, path) => {
      if (!relSpec(table, n) || n.k.length !== 2) return;
      for (const s of [0, 1]) {
        const side = n.k[s],
          other = n.k[1 - s];
        const terms = side.t === R.add ? side.k : [side];
        terms.forEach((t, i) => {
          if (t.t === 'Num' && t.p === 0) return;
          const nt = negL(t, R);
          const rest = terms.filter((_, j) => j !== i);
          const src =
            rest.length === 0
              ? (unitOf(table, R.add) ?? num(0))
              : rest.length === 1
                ? rest[0]
                : light(mk(R.add, rest), table);
          const oRaw = light(mk(R.add, [other, nt]), table);
          const put = (a, b) => {
            const k = [];
            k[s] = a;
            k[1 - s] = b;
            out.push(light(setAt(L, path, mk(n.t, k, n.name)), table));
          };
          put(src, tidy(oRaw, table));
          put(src, oRaw);
          put(light(mk(R.add, [side, nt]), table), oRaw);
        });
      }
    });
    return out;
  },
  // Deux membres multipliés / divisés par un même nombre non nul (sens inversé s'il est négatif)
  relation_mul(L, table, K) {
    const R = roles(table),
      out = [];
    const pred = K.pred;
    eachPos(L, (n, path) => {
      const spec = relSpec(table, n);
      if (!spec || n.k.length !== 2) return;
      const symmetric = !spec.flip;
      const signOf = (c) => {
        if (c.t === 'Num') return Math.sign(c.p);
        if (pred?.positive?.(c)) return 1;
        if (pred?.negative?.(c)) return -1;
        if (symmetric && pred?.nonzero?.(c)) return 2; // non nul, signe inconnu : seulement = et ≠
        return 0;
      };
      const put = (t, s, a, b) => {
        const k = [];
        k[s] = a;
        k[1 - s] = b;
        out.push(light(setAt(L, path, mk(t, k, n.name)), table));
      };
      for (const s of [0, 1]) {
        const side = n.k[s],
          other = n.k[1 - s];
        let factors = null;
        if (side.t === R.neg) factors = mulView(side, R).k;
        else if (side.t === R.mul) factors = side.k;
        if (factors && R.div) {
          // diviseurs candidats : chaque facteur, le coefficient numérique entier (−8 dans −8x),
          // le produit de tous les facteurs sans la cible (2a dans 2at)
          const cands = factors.map((c, i) => ({ c, drop: [i] }));
          const numIdx = factors.map((c, i) => (c.t === 'Num' ? i : -1)).filter((i) => i >= 0);
          if (numIdx.length >= 2) {
            let v = num(1);
            for (const i of numIdx) v = Q.mul(v, factors[i]);
            cands.push({ c: num(v.p, v.q), drop: numIdx });
          }
          if (K.target) {
            const freeIdx = factors
              .map((c, i) => (symbols(c).has(K.target) ? -1 : i))
              .filter((i) => i >= 0);
            if (freeIdx.length >= 2 && freeIdx.length < factors.length) {
              cands.push({
                c: light(
                  mk(
                    R.mul,
                    freeIdx.map((i) => factors[i]),
                  ),
                  table,
                ),
                drop: freeIdx,
              });
            }
          }
          cands.forEach(({ c, drop }) => {
            if (K.target && symbols(c).has(K.target)) return;
            if (c.t === 'Num' && c.p === c.q) return;
            const sg = signOf(c);
            if (!sg) return;
            const t = sg < 0 ? (spec.flip ?? n.t) : n.t;
            const rest = factors.filter((_, j) => !drop.includes(j));
            const restN =
              rest.length === 0 ? num(1) : rest.length === 1 ? rest[0] : mk(R.mul, rest);
            const oDiv = light(mk(R.div, [other, c]), table);
            put(t, s, light(restN, table), foldAll(oDiv, table));
            put(t, s, light(restN, table), oDiv);
            if (other.t === R.mul) {
              const j = other.k.findIndex((f) => keyOf(f) === keyOf(c));
              if (j >= 0) {
                const ok = other.k.filter((_, q) => q !== j);
                put(
                  t,
                  s,
                  light(restN, table),
                  light(ok.length === 1 ? ok[0] : mk(R.mul, ok), table),
                );
              }
            }
            put(t, s, light(mk(R.div, [side, c]), table), oDiv);
          });
        }
        if (side.t === R.div && R.mul) {
          const c = side.k[1];
          if (K.target && symbols(c).has(K.target)) continue;
          const sg = signOf(c);
          if (!sg) continue;
          const t = sg < 0 ? (spec.flip ?? n.t) : n.t;
          const oMul = light(mk(R.mul, [other, c]), table);
          put(t, s, side.k[0], foldAll(oMul, table));
          put(t, s, side.k[0], oMul);
        }
      }
      // chasser un dénominateur numérique d'une somme : « x ÷ 2 + 4 = 10 → x + 8 = 20 » (chaque terme multiplié et
      // calculé, dans le même geste)
      const dens = new Set();
      for (const side of n.k) {
        const terms = side.t === R.add ? side.k : [side];
        for (const t of terms)
          visit(t, (x) => {
            if (
              !isLeaf(x) &&
              x.t === R.div &&
              x.k[1].t === 'Num' &&
              x.k[1].q === 1 &&
              x.k[1].p > 1 &&
              x.k[1].p <= 100
            )
              dens.add(x.k[1].p);
          });
      }
      if (dens.size && n.k.some((side) => side.t === R.add)) {
        const lcm = [...dens].reduce((a, b) => (a * b) / gcd(a, b), 1);
        for (const d of new Set([...dens, lcm])) {
          const times = (side) => {
            const terms = side.t === R.add ? side.k : [side];
            const k = terms.map((t) => calcNorm(light(mk(R.mul, [t, num(d)]), table), table));
            return light(k.length === 1 ? k[0] : mk(R.add, k), table);
          };
          out.push(light(setAt(L, path, mk(n.t, n.k.map(times), n.name)), table));
        }
      }
    });
    return out;
  },
  // Échanger les deux membres (a ⋈ b → b ⋈' a, ⋈' = flip de la table, ou ⋈ s'il est symétrique)
  swap(L, table) {
    const out = [];
    eachPos(L, (n, path) => {
      const spec = relSpec(table, n);
      if (!spec) return;
      out.push(light(setAt(L, path, mk(spec.flip ?? n.t, n.k.slice().reverse(), n.name)), table));
    });
    return out;
  },
};

// ── Analyse : outils des gestes ───────────────────────────────────────────
export const isDer = (n, table) => !isLeaf(n) && table.nodes[n.t]?.eval === 'derive';
export function isLim(n, table) {
  return (
    !isLeaf(n) &&
    table.nodes[n.t]?.eval === 'call' &&
    table.syntax.functions[n.name]?.eval === 'limit'
  );
}
function limitPoint(p, table) {
  const { point } = pointOf(p, table);
  return typeof point === 'number' ? point : Number(point.p) / Number(point.q);
}
// remplace TOUTES les dérivées (u)′ transformables par f(u, v) ; null si aucune
function mapDer(L, table, f) {
  let changed = false;
  const go = (n) => {
    if (isLeaf(n)) return n;
    if (isDer(n, table)) {
      const r = f(n.k[0], varOf(n.k[0], table));
      if (r) {
        changed = true;
        return r;
      }
    }
    const k = n.k.map(go);
    return k.every((c, i) => c === n.k[i]) ? n : mk(n.t, k, n.name);
  };
  const r = go(L);
  return changed ? r : null;
}
const freeOfV = (n, v) => !v || !symbols(n).has(v);
function derOf(u, table) {
  const t = Object.keys(table.nodes).find((x) => table.nodes[x].eval === 'derive');
  return mk(t, [u]);
}
function linearParts(u, v, table, R) {
  if (!v) return null;
  const D = (x) => {
    if (x.t === 'Num' && x.p < 0) return mk(R.neg, [derOf(num(-x.p, x.q), table)]);
    if (freeOfV(x, v)) return derOf(x, table);
    if (x.t === R.neg) return mk(R.neg, [D(x.k[0])]);
    if (x.t === R.mul) {
      const c = x.k.filter((f) => freeOfV(f, v)),
        w = x.k.filter((f) => !freeOfV(f, v));
      if (c.length && w.length)
        return mk(R.mul, [...c, derOf(w.length === 1 ? w[0] : mk(R.mul, w), table)]);
    }
    // (u ÷ k)′ = (u)′ ÷ k : un dénominateur constant reste
    if (x.t === R.div && freeOfV(x.k[1], v) && !freeOfV(x.k[0], v))
      return mk(R.div, [derOf(x.k[0], table), x.k[1]]);
    return derOf(x, table);
  };
  if (u.t === R.add) return mk(R.add, u.k.map(D));
  if (u.t === R.neg || u.t === R.mul || u.t === R.div) {
    const r = D(u);
    return keyOf(light(r, table)) === keyOf(light(derOf(u, table), table)) ? null : r;
  }
  return null;
}
function refDerivative(u, v, table) {
  const R = rolesOf(table);
  const isV = (x) => x.t === 'Sym' && x.name === v;
  if (freeOfV(u, v)) return num(0);
  if (isV(u)) return num(1);
  const ev = table.nodes[u.t]?.eval;
  if (ev === 'pow' && isV(u.k[0]) && u.k[1].t === 'Num') {
    const k = num(u.k[1].p, u.k[1].q),
      k1 = Q.add(k, num(-1));
    const pw = k1.p === 0 ? null : k1.p === k1.q ? u.k[0] : mk(R.pow, [u.k[0], num(k1.p, k1.q)]);
    return pw ? mk(R.mul, [num(k.p, k.q), pw]) : num(k.p, k.q);
  }
  if (ev === 'pow' && u.k[0].t === 'Sym' && u.k[0].name === R.e && isV(u.k[1])) return u;
  if (ev === 'sqrt' && isV(u.k[0])) return mk(R.div, [num(1), mk(R.mul, [num(2), u])]);
  if (ev === 'div' && u.k[0].t === 'Num' && u.k[0].p === 1 && u.k[0].q === 1) {
    const d = u.k[1];
    if (isV(d)) return mk(R.neg, [mk(R.div, [num(1), mk(R.pow, [d, num(2)])])]);
    if (table.nodes[d.t]?.eval === 'pow' && isV(d.k[0]) && d.k[1].t === 'Num' && d.k[1].q === 1) {
      const k = d.k[1].p;
      return mk(R.neg, [mk(R.div, [num(k), mk(R.pow, [d.k[0], num(k + 1)])])]);
    }
  }
  if (ev === 'call' && u.k.length === 1 && isV(u.k[0])) {
    const f = table.syntax.functions[u.name]?.eval;
    if (f === 'exp') return u;
    if (f === 'ln') return mk(R.div, [num(1), u.k[0]]);
    if (f === 'sqrt') return mk(R.div, [num(1), mk(R.mul, [num(2), u])]);
    if (f === 'sin' && R.fn.cos) return mk(R.call, [u.k[0]], R.fn.cos);
    if (f === 'cos' && R.fn.sin) return mk(R.neg, [mk(R.call, [u.k[0]], R.fn.sin)]);
    if (f === 'arctan')
      return mk(R.div, [num(1), mk(R.add, [num(1), mk(R.pow, [u.k[0], num(2)])])]);
  }
  return null;
}

function limitValues(L, table, cc) {
  const out = [];
  const valueOf = (n) => {
    // une limite lim(u ; p)
    if (isLim(n, table)) {
      const [u, p] = n.k;
      const v = varOf(u, table);
      const { point, side } = pointOf(p, table);
      if (Number.isNaN(toNum(point))) return null;
      const plain = limitSym(u, v, point, table, {}, { cc: false, side });
      // limite nulle en un point fini, de signe constant : 0⁺ ou 0⁻ (le signe compte pour la suite : 7 ÷ 0⁺ = +∞)
      if (!cc && plain && !plain.fi && !plain.none) {
        const z = signedZero(plain.v, u, v, point, {}, table, side);
        if (typeof z === 'number' && z === 0) return sideZeroAst(z, table);
      }
      if (cc) {
        if (!plain?.fi) return null;
        const used = {};
        const r = limitSym(u, v, point, table, {}, { cc: true, used, side });
        return r && !r.fi && !r.none && used.cc ? extendedAst(r.v, table) : null;
      }
      return plain && !plain.fi && !plain.none ? extendedAst(plain.v, table) : null;
    }
    // un calcul sur l'infini, sans variable : 3 × (+∞), +∞ + 2, 5 ÷ (+∞)
    if (
      cc ||
      isLeaf(n) ||
      !(hasInf(n, table) || hasSide(n, table)) ||
      [...symbols(n)].some((s) => !table.syntax.constants[s])
    )
      return null;
    if (isSide(n, table)) return null;
    if (n.t === roles(table).neg) return null;
    const r = hasSide(n, table) ? { v: evaluate(n, {}, table) } : limitSym(n, null, 0, table);
    if (r && typeof r.v === 'number' && Number.isNaN(r.v)) return null;
    return r && !r.fi && !r.none ? extendedAst(r.v, table) : null;
  };
  eachPos(L, (n, path) => {
    const r = valueOf(n);
    if (r) out.push(light(setAt(L, path, r), table));
  });
  // toutes à la fois : (1) jusqu'au bout ; (2) en ne calculant que ce qui donne un nombre fini — l'élève écrit
  // « +∞ × (1 − 0 + 0) » avant de conclure « +∞ »
  const allOf = (finiteOnly) => {
    let changed = false;
    const go = (n) => {
      if (isLeaf(n)) return n;
      const r = valueOf(n);
      if (r) {
        changed = true;
        return r;
      }
      const k = n.k.map(go);
      const x = k.every((c, i) => c === n.k[i]) ? n : light(mk(n.t, k, n.name), table);
      const r2 = x !== n ? valueOf(x) : null;
      return r2 && !(finiteOnly && hasInf(r2, table)) ? r2 : x;
    };
    const all = go(L);
    return changed ? light(all, table) : null;
  };
  if (out.length > 1)
    for (const a of [allOf(false), allOf(true)])
      if (a && !out.some((o) => keyOf(o) === keyOf(a))) out.push(a);
  return out;
}
export function hasInf(n, table) {
  const R = rolesOf(table);
  if (n.t === 'Sym') return n.name === R.inf;
  return !isLeaf(n) && n.k.some((c) => hasInf(c, table));
}

// monôme c · vᵃ (c rationnel, a rationnel) ou null
function monoOf(t, v, R) {
  if (t.t === 'Num') return { c: num(t.p, t.q), a: num(0) };
  if (t.t === 'Sym') return t.name === v ? { c: num(1), a: num(1) } : null;
  if (t.t === R.neg) {
    const m = monoOf(t.k[0], v, R);
    return m && { c: Q.neg(m.c), a: m.a };
  }
  if (t.t === R.pow && t.k[0].t === 'Sym' && t.k[0].name === v && t.k[1].t === 'Num')
    return { c: num(1), a: num(t.k[1].p, t.k[1].q) };
  if (t.t === R.mul) {
    let acc = { c: num(1), a: num(0) };
    for (const f of t.k) {
      const m = monoOf(f, v, R);
      if (!m) return null;
      acc = { c: Q.mul(acc.c, m.c), a: Q.add(acc.a, m.a) };
    }
    return acc;
  }
  if (t.t === R.div) {
    const a = monoOf(t.k[0], v, R),
      b = monoOf(t.k[1], v, R);
    if (!a || !b || b.c.p === 0) return null;
    return { c: Q.div(a.c, b.c), a: Q.add(a.a, Q.neg(b.a)) };
  }
  return null;
}
const coefAst = (c, R) =>
  c.q === 1
    ? num(c.p)
    : c.p < 0
      ? mk(R.neg, [mk(R.div, [num(-c.p), num(c.q)])])
      : mk(R.div, [num(c.p), num(c.q)]);
function powerAst(v, a, R) {
  if (a.p === 0) return null;
  const x = { t: 'Sym', name: v };
  return a.p === a.q ? x : mk(R.pow, [x, num(a.p, a.q)]);
}
// c · vᵃ écrit comme l'élève : 3, −5 ÷ n, 1 ÷ n², 3n
function monoAst(m, v, R) {
  const neg = m.c.p < 0,
    c = { p: Math.abs(m.c.p), q: m.c.q };
  let r;
  if (m.a.p === 0) r = coefAst(c, R);
  else if (m.a.p > 0) {
    const pw = powerAst(v, m.a, R);
    r = c.p === c.q ? pw : mk(R.mul, [coefAst(c, R), pw]);
  } else {
    const pw = powerAst(v, Q.neg(m.a), R);
    r = mk(R.div, [num(c.p), c.q === 1 ? pw : mk(R.mul, [num(c.q), pw])]);
  }
  return neg ? mk(R.neg, [r]) : r;
}
// somme → { T: facteur dominant, rest: somme des termes divisés par T, mono: monôme de T }
function factorDominant(sum, v, point, table, R) {
  const terms = sum.k;
  const monos = terms.map((t) => monoOf(t, v, R));
  if (monos.every(Boolean)) {
    // polynôme (puissances rationnelles) : le terme de plus haut degré
    let top = 0;
    monos.forEach((m, i) => {
      if (Q.cmp(m.a, monos[top].a) > 0) top = i;
    });
    const a = monos[top].a;
    if (a.p <= 0) return null;
    const rest = mk(
      R.add,
      monos.map((m) => monoAst({ c: m.c, a: Q.add(m.a, Q.neg(a)) }, v, R)),
    );
    return { T: powerAst(v, a, R), rest, mono: { c: num(1), a } };
  }
  // sinon : le terme de plus forte croissance (exponentielle > puissance > logarithme)
  const s = point > 0 ? 1 : -1;
  const gs = terms.map((t) => growth(t, v, table));
  if (gs.some((g) => !g)) return null;
  const rank = (g) => [g.E * s, g.P * (s > 0 ? 1 : 1), g.G];
  let top = 0;
  const cmpR = (x, y) => {
    for (let i = 0; i < 3; i++) if (x[i] !== y[i]) return x[i] - y[i];
    return 0;
  };
  gs.forEach((g, i) => {
    if (cmpR(rank(g), rank(gs[top])) > 0) top = i;
  });
  const T = terms[top].t === R.neg ? terms[top].k[0] : terms[top];
  const rest = mk(
    R.add,
    terms.map((t, i) =>
      i === top ? (terms[top].t === R.neg ? num(-1) : num(1)) : mk(R.div, [t, T]),
    ),
  );
  return { T, rest, mono: null };
}

function splitDeep(u, v, p, limNode, table, R) {
  const lim = (x) => mk(limNode.t, p ? [x, p] : [x], limNode.name);
  const go = (x) => {
    if (!v || !symbols(x).has(v)) return x;
    if (x.t === R.add || x.t === R.mul || x.t === R.div || x.t === R.neg) {
      const r = mk(x.t, x.k.map(go));
      return indeterminateOf(r, table) ? lim(x) : r;
    }
    return lim(x);
  };
  const r = go(u);
  return isLim(r, table) ? null : r;
}

const isSide = (n, table) => !isLeaf(n) && ['right', 'left'].includes(table.nodes[n.t]?.eval);
export function hasSide(n, table) {
  return isSide(n, table) || (!isLeaf(n) && n.k.some((c) => hasSide(c, table)));
}
